import jwtDecode from 'jwt-decode';

import { Token } from 'src/@types/auth/auth';
import { AuthUser } from 'src/@types/auth/user';

// ----------------------------------------------------------------------
// Utility functions for client-side JWT handling (no signing/verifying on browser)

/**
 * Checks if a JWT access token is still valid based on its exp field
 */
export const isValidToken = (accessToken: string): boolean => {
  if (!accessToken) {
    return false;
  }
  // Decode expiration without verifying signature
  const decoded = jwtDecode<{ exp: number }>(accessToken);
  const currentTime = Date.now() / 1000;
  return decoded.exp > currentTime;
};

/**
 * Persist or clear tokens in localStorage
 */
export const setSession = (token: Token | null): void => {
  if (token) {
    localStorage.setItem('accessToken', token.accessToken);
    localStorage.setItem('refreshToken', token.refreshToken);
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
};

/**
 * Retrieve session tokens and decode user info
 */
export const getSession = (): Token => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const user = accessToken ? decodeToken(accessToken) : null;

  return {
    accessToken,
    refreshToken,
    user,
  } as Token;
};

/**
 * Decode a JWT to an AuthUser instance
 */
export const decodeToken = (token: string): AuthUser => {
  const userInfo = jwtDecode<any>(token);
  const user = new AuthUser();
  user.sub = userInfo.sub;
  user.email = userInfo.email;
  user.firstName = userInfo.given_name;
  user.lastName = userInfo.family_name;
  user.locale = userInfo.locale;
  user.isTenant = userInfo.isTenant;
  user.roles = Array.isArray(userInfo.role) ? userInfo.role : userInfo.role ? [userInfo.role] : [];
  user.permissions = Array.isArray(userInfo.permission)
    ? userInfo.permission.map((p: string) => +p)
    : userInfo.permission
      ? [+userInfo.permission]
      : [];
  return user;
};
